import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  LinearProgress,
  Button,
  Select,
  FormControl,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { startOfDay, startOfWeek, startOfMonth, isAfter } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import TaskCard from './TaskCard';
import taskService from '../../services/taskService';

const COLUMNS = [
  { id: 'todo', title: 'Chờ xử lý', color: '#64748b', bgColor: '#f8fafc' },
  { id: 'in_progress', title: 'Đang làm', color: '#3b82f6', bgColor: '#eff6ff' },
  { id: 'review', title: 'Chờ review', color: '#f59e0b', bgColor: '#fffbeb' },
  { id: 'done', title: 'Hoàn thành', color: '#10b981', bgColor: '#ecfdf5' },
];

// Droppable Column Component
const DroppableColumn = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        minHeight: 200,
        transition: 'background-color 0.2s',
        bgcolor: isOver ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
        borderRadius: 2,
      }}
    >
      {children}
    </Box>
  );
};

const DONE_FILTERS = [
  { value: 'week', label: 'Tuần này' },
  { value: 'month', label: 'Tháng này' },
  { value: 'all', label: 'Tất cả' },
];

const INITIAL_LIMIT = 10;

const TaskKanbanBoard = ({ tasks, loading, onTaskClick, onCreateTask, onTaskUpdated, onTaskDeleted }) => {
  const [activeTask, setActiveTask] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Done column filter & limit
  const [doneFilter, setDoneFilter] = useState('week');
  const [doneLimit, setDoneLimit] = useState(INITIAL_LIMIT);
  const [filterAnchor, setFilterAnchor] = useState(null);

  // Filter done tasks by time period
  const filteredDoneTasks = useMemo(() => {
    const doneTasks = tasks.done || [];
    if (doneFilter === 'all') return doneTasks;

    const now = new Date();
    let startDate;
    
    if (doneFilter === 'today') {
      startDate = startOfDay(now);
    } else if (doneFilter === 'week') {
      startDate = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    } else if (doneFilter === 'month') {
      startDate = startOfMonth(now);
    }

    return doneTasks.filter(task => {
      const completedDate = task.updatedAt ? new Date(task.updatedAt) : new Date(task.createdAt);
      return isAfter(completedDate, startDate);
    });
  }, [tasks.done, doneFilter]);

  // Get visible done tasks (with limit)
  const visibleDoneTasks = useMemo(() => {
    return filteredDoneTasks.slice(0, doneLimit);
  }, [filteredDoneTasks, doneLimit]);

  const hasMoreDoneTasks = filteredDoneTasks.length > doneLimit;
  const remainingDoneTasks = filteredDoneTasks.length - doneLimit;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event) => {
    const { active } = event;
    const task = findTaskById(active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const draggedTask = findTaskById(active.id);
    if (!draggedTask) return;

    // Determine the target status
    let targetStatus = null;
    
    // Check if dropped on a column
    const columnIds = COLUMNS.map(c => c.id);
    if (columnIds.includes(over.id)) {
      targetStatus = over.id;
    } else {
      // Dropped on a task, get that task's status
      const overTask = findTaskById(over.id);
      if (overTask) {
        targetStatus = overTask.status;
      }
    }

    if (!targetStatus) return;

    // If status changed, update the task
    if (draggedTask.status !== targetStatus) {
      try {
        const result = await taskService.updateTaskStatus(draggedTask._id, targetStatus);
        if (result.success) {
          onTaskUpdated?.(result.data);
        }
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    }
  };

  const findTaskById = (id) => {
    for (const status of Object.keys(tasks)) {
      const task = tasks[status]?.find(t => t._id === id);
      if (task) return task;
    }
    return null;
  };

  const handleMenuOpen = (event, task) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTask(null);
  };

  const handleViewTask = () => {
    onTaskClick?.(selectedTask);
    handleMenuClose();
  };

  const handleDeleteTask = async () => {
    if (selectedTask && window.confirm('Bạn có chắc muốn xóa task này?')) {
      try {
        await taskService.deleteTask(selectedTask._id);
        onTaskDeleted?.(selectedTask._id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          overflowX: 'auto',
          pb: 2,
          minHeight: 'calc(100vh - 280px)',
          px: 0.5,
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: '#f1f5f9',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#cbd5e1',
            borderRadius: 4,
            '&:hover': { bgcolor: '#94a3b8' },
          },
        }}
      >
        {COLUMNS.map((column) => {
          // Use filtered tasks for done column, normal tasks for others
          const isDoneColumn = column.id === 'done';
          const columnTasks = isDoneColumn ? visibleDoneTasks : (tasks[column.id] || []);
          const totalCount = isDoneColumn ? filteredDoneTasks.length : columnTasks.length;
          const taskIds = columnTasks.map(t => t._id);

          return (
            <Paper
              key={column.id}
              sx={{
                minWidth: { xs: 280, sm: 300, md: 320 },
                maxWidth: { xs: 280, sm: 300, md: 320 },
                bgcolor: column.bgColor,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              {/* Column Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: `2px solid ${column.color}30`,
                  bgcolor: 'white',
                  borderRadius: '12px 12px 0 0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: column.color,
                      boxShadow: `0 0 0 3px ${column.color}20`,
                    }}
                  />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700, 
                      color: '#1e293b',
                      fontSize: '0.95rem',
                    }}
                  >
                    {column.title}
                  </Typography>
                  <Chip
                    label={totalCount}
                    size="small"
                    sx={{
                      height: 22,
                      minWidth: 22,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: column.color,
                      color: 'white',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                </Box>

                {column.id === 'todo' && (
                  <IconButton
                    size="small"
                    onClick={() => onCreateTask?.()}
                    sx={{
                      bgcolor: '#667eea',
                      color: 'white',
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: '#5a67d8', transform: 'scale(1.05)' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}

                {/* Filter for Done column */}
                {isDoneColumn && (
                  <>
                    <Chip
                      icon={<FilterIcon sx={{ fontSize: 14 }} />}
                      label={DONE_FILTERS.find(f => f.value === doneFilter)?.label}
                      size="small"
                      onClick={(e) => setFilterAnchor(e.currentTarget)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: '#10b98120',
                        color: '#10b981',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        '&:hover': { bgcolor: '#10b98130' },
                      }}
                    />
                    <Menu
                      anchorEl={filterAnchor}
                      open={Boolean(filterAnchor)}
                      onClose={() => setFilterAnchor(null)}
                    >
                      {DONE_FILTERS.map((filter) => (
                        <MenuItem
                          key={filter.value}
                          selected={doneFilter === filter.value}
                          onClick={() => {
                            setDoneFilter(filter.value);
                            setDoneLimit(INITIAL_LIMIT);
                            setFilterAnchor(null);
                          }}
                        >
                          {filter.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>

              {/* Tasks List */}
              <Box
                sx={{
                  p: 1.5,
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 200,
                }}
              >
                <DroppableColumn id={column.id}>
                  <SortableContext
                    items={taskIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {columnTasks.map((task) => (
                      <TaskCard
                        key={task._id}
                        task={task}
                        onClick={onTaskClick}
                        onMenuClick={handleMenuOpen}
                      />
                    ))}
                  </SortableContext>

                  {/* Load More Button for Done column */}
                  {isDoneColumn && hasMoreDoneTasks && (
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => setDoneLimit(prev => prev + 10)}
                      sx={{
                        mt: 1.5,
                        py: 1,
                        color: '#10b981',
                        bgcolor: '#10b98110',
                        borderRadius: 2,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        '&:hover': { bgcolor: '#10b98120' },
                      }}
                      endIcon={<ExpandMoreIcon />}
                    >
                      Xem thêm {remainingDoneTasks} tasks
                    </Button>
                  )}

                  {columnTasks.length === 0 && (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: 'center',
                        color: 'text.secondary',
                        border: '2px dashed #e2e8f0',
                        borderRadius: 2,
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {isDoneColumn ? 'Không có task hoàn thành' : 'Kéo thả task vào đây'}
                      </Typography>
                    </Box>
                  )}
                </DroppableColumn>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeTask && (
          <Box sx={{ opacity: 0.8, transform: 'rotate(3deg)' }}>
            <TaskCard task={activeTask} />
          </Box>
        )}
      </DragOverlay>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleViewTask}>
          <ViewIcon sx={{ mr: 1, fontSize: 20 }} />
          Xem chi tiết
        </MenuItem>
        <MenuItem onClick={handleViewTask}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Chỉnh sửa
        </MenuItem>
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Xóa
        </MenuItem>
      </Menu>
    </DndContext>
  );
};

export default TaskKanbanBoard;
